import React, { useEffect, useState, useCallback } from "react";
import { useVenue } from "@/components/shared/VenueContext";
import { wasteApi } from "@/api/waste";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bell, Check, AlertTriangle, Trash2 } from "lucide-react";
import { toast } from "sonner";

import WasteDashboard from "@/components/waste/WasteDashboard";
import WasteQuickForm from "@/components/waste/WasteQuickForm";
import WasteHistory from "@/components/waste/WasteHistory";
import WasteCriticalProducts from "@/components/waste/WasteCriticalProducts";
import WasteReuse from "@/components/waste/WasteReuse";
import WasteSettings from "@/components/waste/WasteSettings";

function AlertsBanner({ venueId, refreshKey }) {
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(async () => {
    if (!venueId) return;
    try {
      const data = await wasteApi.listAlerts(venueId, "active");
      setAlerts(data || []);
    } catch (e) { /* noop */ }
  }, [venueId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const ack = async (id) => {
    try {
      await wasteApi.acknowledgeAlert(id);
      toast.success("Alerta resolta");
      load();
    } catch (e) { toast.error("Error"); }
  };

  if (!alerts.length) return null;
  return (
    <Card className="p-3 mb-4 bg-amber-50 border-amber-200">
      <div className="flex items-center gap-2 mb-2">
        <Bell className="w-4 h-4 text-amber-600" />
        <p className="font-semibold text-amber-900 text-sm">Alertes actives</p>
        <Badge className="bg-amber-200 text-amber-900">{alerts.length}</Badge>
      </div>
      <div className="space-y-2 max-h-40 overflow-y-auto">
        {alerts.slice(0, 5).map((a) => (
          <div key={a.waste_alert_id} className="flex items-start justify-between gap-2 bg-white rounded-lg p-2 text-sm">
            <div>
              <p className="font-semibold text-gray-900">{a.title}</p>
              <p className="text-xs text-gray-500">{a.message}</p>
            </div>
            <Button size="sm" variant="ghost" className="h-7" onClick={() => ack(a.waste_alert_id)} data-testid={`alert-ack-${a.waste_alert_id}`}>
              <Check className="w-3.5 h-3.5 mr-1" /> OK
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function WasteManagement() {
  const { currentVenue, loading: venueLoading } = useVenue();
  const venueId = currentVenue?.venue_id || currentVenue?.id;
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [refreshKey, setRefreshKey] = useState(0);

  const loadSettings = useCallback(async () => {
    if (!venueId) return;
    try {
      const s = await wasteApi.getSettings(venueId);
      setSettings(s);
    } catch (e) { /* noop */ }
  }, [venueId]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  if (venueLoading || !venueId) return <QuickLoader />;
  if (!settings) return <QuickLoader />;

  const onSaved = () => setRefreshKey((k) => k + 1);

  return (
    <div data-testid="waste-management-page">
      <PageHeader
        title="Malbaratament alimentari"
        subtitle="Registra, controla i redueix la merma. Estalvia costos i impacte ambiental."
      />

      <AlertsBanner venueId={venueId} refreshKey={refreshKey} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap gap-1 mb-4 h-auto">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="register" data-testid="tab-register">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Registrar
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Històric</TabsTrigger>
          <TabsTrigger value="critical" data-testid="tab-critical">
            <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Crítics
          </TabsTrigger>
          <TabsTrigger value="reuse" data-testid="tab-reuse">Reutilització</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Configuració</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <WasteDashboard venueId={venueId} key={`dash-${refreshKey}`} />
        </TabsContent>
        <TabsContent value="register">
          <div className="max-w-xl mx-auto">
            <WasteQuickForm venueId={venueId} settings={settings} onSaved={onSaved} />
          </div>
        </TabsContent>
        <TabsContent value="history">
          <WasteHistory venueId={venueId} settings={settings} refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="critical">
          <WasteCriticalProducts venueId={venueId} settings={settings} refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="reuse">
          <WasteReuse venueId={venueId} settings={settings} />
        </TabsContent>
        <TabsContent value="settings">
          <WasteSettings venueId={venueId} settings={settings} onUpdated={loadSettings} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
